export type LiveStatus = "live" | "upcoming" | "replay";

export interface LiveItem {
  id: string;
  title: string;
  category: string;
  host: string;
  location: string;
  status: LiveStatus;
  viewers: number;
  likes: number;
  signal: number;
  sound: boolean;
  startAt: string;
  duration: string;
  cover: string;
  summary: string;
  tags: string[];
  highlights: string[];
  featured: boolean;
}

export interface LiveCategory {
  id: string;
  name: string;
  icon: string;
  desc: string;
  count: number;
}

export const liveCategories: LiveCategory[] = [
  {
    id: "breaking",
    name: "突发现场",
    icon: "ri-alarm-warning-line",
    desc: "第一时间接通现场信号，事态进展随镜头同步推进。",
    count: 38,
  },
  {
    id: "livelihood",
    name: "民生追踪",
    icon: "ri-community-line",
    desc: "社区、菜场、街巷里的真实诉求，镜头长期跟进。",
    count: 26,
  },
  {
    id: "traffic",
    name: "城市路况",
    icon: "ri-roadster-line",
    desc: "早晚高峰、封路检修、积水堵点，画面即路况。",
    count: 21,
  },
  {
    id: "accident",
    name: "事故直击",
    icon: "ri-first-aid-kit-line",
    desc: "救援与处置的现场实录，只呈现画面不渲染情绪。",
    count: 17,
  },
  {
    id: "weather",
    name: "天气预警",
    icon: "ri-cloud-windy-line",
    desc: "台风、暴雨、山火等极端天气的前沿观测与值守。",
    count: 19,
  },
  {
    id: "talk",
    name: "现场连麦",
    icon: "ri-mic-2-line",
    desc: "把当事人、目击者请进直播间，声音还原经过。",
    count: 24,
  },
];

export const liveSessions: LiveItem[] = [
  {
    id: "live-001",
    title: "城中村改造现场：居民连夜搬离实况",
    category: "突发现场",
    host: "现场信号组",
    location: "华东 · 城北片区",
    status: "live",
    viewers: 128400,
    likes: 8620,
    signal: 98,
    sound: true,
    startAt: "09:40 开播",
    duration: "已直播 3 小时 12 分",
    cover:
      "https://readdy.ai/api/search-image?query=Cinematic%20dark%20photojournalism%20of%20an%20old%20urban%20village%20alley%20at%20night%20with%20moving%20trucks%20and%20warm%20street%20lamps%2C%20moody%20low-key%20lighting%2C%20deep%20charcoal%20tones%20with%20amber%20glow%2C%20high%20detail%2C%20subtle%20film%20grain%2C%20editorial%20news%20broadcast%20still&width=800&height=450&seq=blzb-cover-001&orientation=landscape",
    summary:
      "城北片区最后一排自建房进入搬迁时段，镜头架在巷口全程记录，能看到搬家车辆进出与楼道灯亮灭，连麦里有居民讲述过渡房安排。",
    tags: ["搬迁现场", "实况画面", "连麦声音"],
    highlights: ["巷口机位 24 小时不切", "多户居民连麦讲述过渡安排", "夜间画面已开启低照度增强"],
    featured: true,
  },
  {
    id: "live-002",
    title: "台风外围云系逼近沿海 记者直击码头封港",
    category: "天气预警",
    host: "海岸线值守",
    location: "东南沿海 · 老码头",
    status: "live",
    viewers: 96400,
    likes: 6120,
    signal: 95,
    sound: true,
    startAt: "07:15 开播",
    duration: "已直播 5 小时 38 分",
    cover:
      "https://readdy.ai/api/search-image?query=Dramatic%20dark%20storm%20clouds%20over%20a%20coastal%20harbor%20with%20moored%20fishing%20boats%2C%20heavy%20wind%20and%20wet%20dock%2C%20moody%20desaturated%20charcoal%20and%20slate%20palette%20with%20faint%20amber%20harbor%20lights%2C%20cinematic%20wide%20shot%2C%20high%20detail%20photojournalism&width=800&height=450&seq=blzb-cover-002&orientation=landscape",
    summary:
      "台风外围云系压向海岸，码头作业全面暂停，镜头固定在防波堤高位，持续输出封港后的实时画面与风速读数，连麦里有船主说明系泊加固情况。",
    tags: ["台风预警", "码头封港", "风速读数"],
    highlights: ["防波堤高位机位", "实时风速与浪高播报", "船主连麦说明加固进度"],
    featured: true,
  },
  {
    id: "live-003",
    title: "深夜美食街突发纠纷 连麦当事人还原经过",
    category: "民生追踪",
    host: "夜巡小分队",
    location: "华中 · 老夜市",
    status: "live",
    viewers: 74200,
    likes: 5310,
    signal: 93,
    sound: true,
    startAt: "22:08 开播",
    duration: "已直播 1 小时 06 分",
    cover:
      "https://readdy.ai/api/search-image?query=Late%20night%20street%20food%20market%20filled%20with%20warm%20neon%20stall%20lights%20and%20steam%2C%20crowd%20of%20people%20gathered%20in%20the%20distance%2C%20dark%20surroundings%2C%20moody%20low-key%20cinematic%20lighting%20with%20amber%20and%20orange%20highlights%2C%20high%20detail%20editorial%20photography&width=800&height=450&seq=blzb-cover-003&orientation=landscape",
    summary:
      "美食街中段出现一起摊主与顾客的争执，镜头保持中远景不介入，随后连麦双方还原事情经过，现场秩序已恢复。",
    tags: ["夜市现场", "双方连麦", "秩序恢复"],
    highlights: ["中远景观察不介入", "当事双方先后连麦", "现场秩序已于 23:10 恢复"],
    featured: true,
  },
  {
    id: "live-004",
    title: "早高峰地铁口积水 排水车现场作业画面",
    category: "城市路况",
    host: "通勤观察",
    location: "华北 · 三号线出站口",
    status: "live",
    viewers: 58900,
    likes: 3980,
    signal: 91,
    sound: false,
    startAt: "06:50 开播",
    duration: "已直播 2 小时 41 分",
    cover:
      "https://readdy.ai/api/search-image?query=Early%20morning%20city%20subway%20entrance%20with%20shallow%20flooding%20on%20the%20sidewalk%2C%20drainage%20truck%20hoses%20and%20commuters%20detouring%2C%20overcast%20grey%20dawn%20light%2C%20muted%20charcoal%20palette%20with%20amber%20warning%20cones%2C%20cinematic%20documentary%20style%20high%20detail&width=800&height=450&seq=blzb-cover-004&orientation=landscape",
    summary:
      "三号线出站口一侧积水没过脚面，排水车已到位作业，画面持续展示水位变化与引导标线，通勤人群绕行秩序正常。",
    tags: ["早高峰", "积水点", "排水作业"],
    highlights: ["水位刻度对比画面", "现场已开启声音关闭模式", "绕行通道实时跟随"],
    featured: true,
  },
  {
    id: "live-005",
    title: "高速连环追尾事故 清障车现场处置",
    category: "事故直击",
    host: "路网值守",
    location: "西南 · 绕城高速 K112",
    status: "upcoming",
    viewers: 41200,
    likes: 2210,
    signal: 88,
    sound: true,
    startAt: "14:30 开播",
    duration: "预计 90 分钟",
    cover:
      "https://readdy.ai/api/search-image?query=Highway%20scene%20at%20dusk%20with%20several%20disabled%20cars%20and%20a%20tow%20truck%20working%2C%20flashing%20amber%20warning%20lights%20reflecting%20on%20wet%20asphalt%2C%20dark%20moody%20cinematic%20atmosphere%2C%20charcoal%20tones%2C%20high%20detail%20photojournalism%20wide%20shot&width=800&height=450&seq=blzb-cover-005&orientation=landscape",
    summary:
      "绕城高速 K112 段多车追尾，清障车正按顺序拖离，信号车将于 14:30 接入现场画面，届时同步播报通行恢复情况。",
    tags: ["高速事故", "清障处置", "通行恢复"],
    highlights: ["信号车 14:30 接入", "分车道通行指引", "拖离进度实时播报"],
    featured: false,
  },
  {
    id: "live-006",
    title: "老小区加装电梯施工 业主大会现场",
    category: "民生追踪",
    host: "邻里观察",
    location: "华东 · 红旗二村",
    status: "upcoming",
    viewers: 18700,
    likes: 1040,
    signal: 86,
    sound: true,
    startAt: "16:00 开播",
    duration: "预计 120 分钟",
    cover:
      "https://readdy.ai/api/search-image?query=Older%20residential%20courtyard%20in%20late%20afternoon%20with%20folding%20chairs%20arranged%20for%20a%20neighborhood%20meeting%2C%20construction%20scaffolding%20on%20the%20side%2C%20soft%20warm%20dusk%20light%2C%20muted%20charcoal%20and%20amber%20palette%2C%20cinematic%20documentary%20photography%20high%20detail&width=800&height=450&seq=blzb-cover-006&orientation=landscape",
    summary:
      "红旗二村加装电梯进入方案表决环节，镜头将完整记录业主大会发言与投票过程，连麦保留不同楼层居民的顾虑与回应。",
    tags: ["加装电梯", "业主大会", "方案表决"],
    highlights: ["发言与投票全程不剪辑", "高低楼层观点对等呈现", "表决结果当场公布"],
    featured: false,
  },
  {
    id: "live-007",
    title: "山火过境后的第一夜 巡查队手电画面",
    category: "突发现场",
    host: "山区信号组",
    location: "西南 · 半坡林场",
    status: "replay",
    viewers: 210500,
    likes: 15320,
    signal: 90,
    sound: false,
    startAt: "昨日 21:20",
    duration: "回看 3 小时 05 分",
    cover:
      "https://readdy.ai/api/search-image?query=Night%20time%20hillside%20forest%20ridge%20after%20a%20wildfire%20with%20flashlight%20beams%20from%20patrol%20workers%2C%20faint%20smoke%20haze%2C%20very%20dark%20charcoal%20atmosphere%20with%20amber%20torch%20glow%2C%20cinematic%20documentary%20photojournalism%20high%20detail%20wide%20shot&width=800&height=450&seq=blzb-cover-007&orientation=landscape",
    summary:
      "山火明火扑灭后的首个夜间值守，巡查队沿脊线逐点排查余火，手持设备画面亮度有限，信号组已完成低照度增强后回看存档。",
    tags: ["山火余火", "夜间巡查", "回看存档"],
    highlights: ["12 个值守点逐一排查", "画面已做低照度处理", "完整回看存于档案"],
    featured: false,
  },
  {
    id: "live-008",
    title: "跨江大桥封路检修 双向车流实时镜头",
    category: "城市路况",
    host: "通勤观察",
    location: "华中 · 跨江主桥",
    status: "upcoming",
    viewers: 33800,
    likes: 1870,
    signal: 87,
    sound: false,
    startAt: "22:00 开播",
    duration: "预计 5 小时",
    cover:
      "https://readdy.ai/api/search-image?query=Cable%20stayed%20river%20bridge%20at%20night%20with%20maintenance%20barriers%20and%20lane%20closure%20signs%2C%20sparse%20vehicle%20traffic%2C%20long%20exposure%20light%20trails%2C%20dark%20moody%20cinematic%20charcoal%20palette%20with%20amber%20street%20lamps%2C%20high%20detail&width=800&height=450&seq=blzb-cover-008&orientation=landscape",
    summary:
      "跨江主桥双向夜间封路检修，镜头固定在桥塔观测位，持续输出双向车流绕行画面与封路指示，方便过江通勤提前规划路线。",
    tags: ["封路检修", "过江绕行", "夜间车流"],
    highlights: ["桥塔观测位固定机位", "绕行路线实时标注", "每小时播报一次解封进度"],
    featured: false,
  },
  {
    id: "live-009",
    title: "夜市摊主与城管对话 全程连麦记录",
    category: "现场连麦",
    host: "夜市观察",
    location: "华南 · 沿江夜摊",
    status: "replay",
    viewers: 156700,
    likes: 12040,
    signal: 92,
    sound: true,
    startAt: "前日 20:40",
    duration: "回看 2 小时 18 分",
    cover:
      "https://readdy.ai/api/search-image?query=Riverside%20night%20market%20stalls%20with%20warm%20string%20lights%20and%20a%20calm%20conversation%20between%20two%20people%2C%20dark%20surroundings%20reflecting%20on%20water%2C%20moody%20cinematic%20amber%20and%20charcoal%20palette%2C%20high%20detail%20editorial%20photography&width=800&height=450&seq=blzb-cover-009&orientation=landscape",
    summary:
      "沿江夜摊规范整治过程中，摊主与管理人员就摆放边界进行沟通，连麦完整保留双方发言，最终达成划线经营的一致意见。",
    tags: ["现场连麦", "沟通记录", "划线经营"],
    highlights: ["双方发言完整保留", "连麦声音全程清晰", "沟通结论当场确认"],
    featured: false,
  },
  {
    id: "live-010",
    title: "演唱会散场人潮 出口实时信号",
    category: "现场连麦",
    host: "场馆连线",
    location: "华东 · 体育中心",
    status: "replay",
    viewers: 289300,
    likes: 21860,
    signal: 96,
    sound: true,
    startAt: "前日 22:35",
    duration: "回看 68 分钟",
    cover:
      "https://readdy.ai/api/search-image?query=Large%20stadium%20exit%20at%20night%20with%20dense%20crowd%20leaving%20after%20a%20concert%2C%20glowing%20stage%20lights%20behind%20curved%20architecture%2C%20dark%20moody%20cinematic%20charcoal%20and%20warm%20gold%20palette%2C%20high%20detail%20wide%20angle%20photography&width=800&height=450&seq=blzb-cover-010&orientation=landscape",
    summary:
      "演唱会散场时段，镜头架在正门出口高位，实时呈现四个分流出入口的人流密度，连麦里同步播报地铁加开信息。",
    tags: ["散场人流", "出口信号", "地铁加开"],
    highlights: ["四个出口同屏对比", "人流密度分级提示", "地铁加开信息播报"],
    featured: false,
  },
  {
    id: "live-011",
    title: "暴雨内涝救援 冲锋舟运送物资",
    category: "事故直击",
    host: "应急救援连线",
    location: "华南 · 沿河社区",
    status: "replay",
    viewers: 197600,
    likes: 17920,
    signal: 89,
    sound: true,
    startAt: "前日 04:10",
    duration: "回看 4 小时 26 分",
    cover:
      "https://readdy.ai/api/search-image?query=Flooded%20residential%20street%20at%20dawn%20with%20a%20rescue%20boat%20carrying%20supplies%2C%20water%20reflecting%20faint%20amber%20lights%2C%20overcast%20grey%20rain%2C%20dark%20muted%20cinematic%20palette%2C%20high%20detail%20photojournalism%20documentary%20style&width=800&height=450&seq=blzb-cover-011&orientation=landscape",
    summary:
      "沿河社区低洼段积水较深，救援冲锋舟往返运送饮用水与照明物资，镜头跟随船体视角，连麦里有社区工作人员说明转移安置进度。",
    tags: ["内涝救援", "物资运送", "转移安置"],
    highlights: ["船体视角跟随拍摄", "安置点进度实时播报", "物资清单逐项核对"],
    featured: false,
  },
  {
    id: "live-012",
    title: "农贸市场凌晨进货 菜价实时播报",
    category: "民生追踪",
    host: "菜篮子观察",
    location: "华北 · 城西批发市场",
    status: "replay",
    viewers: 63400,
    likes: 4210,
    signal: 90,
    sound: true,
    startAt: "今日 03:30",
    duration: "回看 1 小时 52 分",
    cover:
      "https://readdy.ai/api/search-image?query=Pre-dawn%20wholesale%20vegetable%20market%20with%20crates%20of%20fresh%20produce%20and%20workers%20sorting%2C%20hanging%20warm%20lamps%20against%20dark%20blue%20hour%2C%20muted%20charcoal%20and%20amber%20cinematic%20palette%2C%20high%20detail%20documentary%20photography&width=800&height=450&seq=blzb-cover-012&orientation=landscape",
    summary:
      "城西批发市场凌晨进场时段，镜头停在主通道口，实时记录叶菜、瓜果的到货量与批发价，连麦里采购商说明当日价格波动原因。",
    tags: ["凌晨进货", "菜价播报", "到货量"],
    highlights: ["主通道口固定机位", "叶菜瓜果价格逐项播报", "采购商连麦解读波动"],
    featured: false,
  },
];

export const liveStatusLabel: Record<LiveStatus, string> = {
  live: "正在直播",
  upcoming: "即将开播",
  replay: "回看中",
};